<template>
  <div class="local-agent-card" :class="`la-${state.status}`">
    <!-- 标题栏:像终端窗口的标签栏 -->
    <div class="la-bar">
      <span class="la-dots" aria-hidden="true"><i></i><i></i><i></i></span>
      <span class="la-bar-title">{{ statusTitle }}</span>
      <span class="la-bar-dir" :title="state.workspace">{{ state.workspace || '…' }}</span>
      <span v-if="state.modelName" class="la-bar-model" :title="`使用你的模型 ${state.modelName},直连不经过雨见服务器`">{{ state.modelName }}</span>
      <span v-if="elapsedText" class="la-bar-time">{{ elapsedText }}</span>
    </div>

    <!-- 控制台 -->
    <div ref="term" class="la-term" @scroll="onTermScroll">
      <div class="la-line la-prompt"><span class="la-ps">$</span> pi <span class="la-arg">{{ state.task }}</span></div>

      <template v-for="(item, i) in log">
        <div v-if="item.kind === 'text'" :key="i" class="la-line la-text">{{ item.text }}</div>

        <div v-else-if="item.kind === 'think'" :key="i" class="la-line la-think">
          <span class="la-tag">思考</span>{{ item.text }}
        </div>

        <div v-else-if="item.kind === 'tool'" :key="i" class="la-tool" :class="`la-tool-${item.status}`">
          <div class="la-tool-head" @click="toggle(item)">
            <span class="la-tool-mark">{{ item.status === 'running' ? '▶' : item.status === 'error' ? '✗' : '✓' }}</span>
            <span class="la-tool-name">{{ toolLabel(item.tool) }}</span>
            <span class="la-tool-sum">{{ item.summary }}</span>
            <span v-if="item.ms != null" class="la-tool-ms">{{ formatMs(item.ms) }}</span>
            <span v-if="item.status === 'running'" class="la-cursor" aria-hidden="true"></span>
          </div>
          <pre v-if="item.detail && (item.open || isShell(item.tool))" class="la-tool-detail">{{ isShell(item.tool) ? '> ' + item.detail : item.detail }}</pre>
          <pre v-if="item.output && (item.open || item.status !== 'ok' || isShell(item.tool))" class="la-tool-out">{{ item.output }}</pre>
          <div v-if="!item.open && (item.detail || item.output) && !isShell(item.tool) && item.status === 'ok'" class="la-tool-more" @click="toggle(item)">展开详情</div>
        </div>

        <div v-else-if="item.kind === 'retry'" :key="i" class="la-line la-warn">↻ 重试 {{ item.attempt }}/{{ item.max }}:{{ item.text }}</div>

        <div v-else-if="item.kind === 'error'" :key="i" class="la-line la-err">✗ {{ item.text }}</div>

        <div v-else-if="item.kind === 'end'" :key="i" class="la-line la-end">{{ item.text }}</div>
      </template>

      <div v-if="state.status === 'starting' || (state.status === 'running' && !runningTool)" class="la-line la-wait">
        <span class="la-cursor" aria-hidden="true"></span>
      </div>
    </div>

    <div v-if="state.error" class="la-error">{{ errorText }}</div>

    <div class="la-actions">
      <button v-if="state.status === 'running'" type="button" class="la-btn" @click.stop="stop">停止</button>
      <button v-if="state.error === 'login_required'" type="button" class="la-btn la-btn-primary" @click.stop="openLogin">登录</button>
      <button v-if="state.sessionId && state.workspace" type="button" class="la-btn" @click.stop="openDir">打开文件夹</button>
      <button v-if="state.error" type="button" class="la-btn" @click.stop="copyLog">{{ copyLabel }}</button>
      <button v-else-if="log.length" type="button" class="la-btn la-btn-link" @click.stop="copyLog">{{ copyState ? copyLabel : '复制记录' }}</button>
      <button v-if="allowAnswerNormally && (state.status === 'starting' || state.status === 'running')" type="button" class="la-btn la-btn-link" @click.stop="answerNormally">改为普通回答</button>
      <span v-if="quotaText" class="la-quota">{{ quotaText }}</span>
    </div>
  </div>
</template>

<script>
// 本地 Agent(浏览器内置 pi)执行卡片,控制台样式。会话由 BrowserAgentLayout.startLocalAgent 创建,
// 这里订阅 window 'rainsee-local-agent' 事件(rainsee-pc src/preloads/ai-browser-bridge.ts、
// src/main/local-agent/session.ts LocalAgentEvent),把执行记录写进 msg.localAgent.log(随会话保存),
// 结束后把 pi 的最终回复写进 msg.content(进入对话历史,卡片下方按 Markdown 显示)。
const TOOL_LABELS = {
  read: '读取', write: '写入', edit: '修改', grep: '搜索', find: '查找', ls: '列目录',
  powershell: 'PowerShell', bash: 'bash', rains_run: '浏览器程序',
  browser_extension_install: '安装扩展', browser_extension_reload: '重载扩展', browser_extension_logs: '扩展日志',
  browser_extension_list: '扩展列表', browser_extension_uninstall: '卸载扩展'
};
const MAX_LOG = 500;
const MAX_TEXT = 20000;

const bridge = () => (window.rainseeBrowser && window.rainseeBrowser.localAgent) || null;

export default {
  name: 'LocalAgentCard',
  props: {
    msg: { type: Object, required: true },
    // 语音任务(VoiceAgentTasks.vue)不属于本页会话:传 {stop, openDir, getDiagnostics} 代替 localAgent 接口
    controller: { type: Object, default: null },
    // 语音任务没有「改为普通回答」
    allowAnswerNormally: { type: Boolean, default: true }
  },
  data() {
    return { quota: null, copyState: '', stickBottom: true, now: Date.now(), timer: null };
  },
  computed: {
    state() {
      return this.msg.localAgent;
    },
    log() {
      return this.state.log || [];
    },
    runningTool() {
      return this.log.some((x) => x.kind === 'tool' && x.status === 'running');
    },
    statusTitle() {
      return {
        starting: '正在启动本地 Agent…',
        running: '本地 Agent 正在执行',
        done: '本地 Agent 已完成',
        stopped: '本地 Agent 已停止',
        error: '本地 Agent 执行失败'
      }[this.state.status] || '本地 Agent';
    },
    elapsedText() {
      if (!this.state.startedAt) return '';
      const end = this.state.endedAt || (['starting', 'running'].includes(this.state.status) ? this.now : 0);
      return end ? this.formatMs(end - this.state.startedAt) : '';
    },
    errorText() {
      const code = this.state.error;
      const detail = this.state.errorMessage;
      if (code === 'daily_limit') return '今日本地 Agent 额度已用完,请明天再试。';
      // 后端关闭未登录领取时(PiKey.php PI_ALLOW_ANONYMOUS=false)
      if (code === 'login_required') return detail || '请登录雨见账号后使用本地 Agent。';
      if (code === 'auth') return '本地 Agent 密钥无效,请重试。';
      if (code === 'blocked') return '请求被服务器防火墙拦截。请复制错误日志反馈给我们。';
      if (code === 'network') return `连接模型服务失败,请稍后重试。${detail ? `(${detail})` : ''}`;
      if (code === 'issue_failed') return `领取本地 Agent 密钥失败:${detail || '网络错误'}`;
      if (code === 'model') return `模型返回错误:${detail || '未知错误'}`;
      return detail || code;
    },
    quotaText() {
      return this.quota ? `今日剩余 ${this.quota.remaining}/${this.quota.limit} 次` : '';
    },
    copyLabel() {
      if (this.copyState === 'done') return '已复制';
      if (this.copyState === 'failed') return '复制失败';
      return this.state.error ? '复制错误日志' : '复制记录';
    }
  },
  watch: {
    'state.status': {
      immediate: true,
      handler(status) {
        const live = status === 'starting' || status === 'running';
        if (live && !this.timer) this.timer = setInterval(() => { this.now = Date.now(); }, 1000);
        if (!live && this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }
    }
  },
  created() {
    // 老记录(没有 log 字段)补上,保证响应式
    if (!Array.isArray(this.state.log)) this.$set(this.state, 'log', []);
    if (!this.state.startedAt) this.$set(this.state, 'startedAt', Date.now());
  },
  mounted() {
    window.addEventListener('rainsee-local-agent', this.onEvent);
    // 页面重载后会话已不在了(浏览器在页面跳转时结束会话)
    if (['running', 'starting'].includes(this.state.status) && !this.state.live) this.state.status = 'stopped';
    if (this.state.status === 'done' || this.state.status === 'error') this.loadQuota();
    this.scrollTerm(true);
  },
  beforeDestroy() {
    window.removeEventListener('rainsee-local-agent', this.onEvent);
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    toolLabel(tool) {
      if (TOOL_LABELS[tool]) return TOOL_LABELS[tool];
      return String(tool || '').startsWith('browser_') ? `浏览器 ${String(tool).slice(8)}` : tool;
    },
    isShell(tool) {
      return tool === 'powershell' || tool === 'bash';
    },
    formatMs(ms) {
      if (ms < 1000) return `${ms}ms`;
      const s = Math.round(ms / 100) / 10;
      if (s < 60) return `${s}s`;
      return `${Math.floor(s / 60)}m${Math.round(s % 60)}s`;
    },
    toggle(item) {
      this.$set(item, 'open', !item.open);
    },
    push(entry) {
      const log = this.state.log;
      log.push(entry);
      if (log.length > MAX_LOG) log.splice(0, log.length - MAX_LOG);
      this.scrollTerm();
    },
    // 连续的文字/思考增量并到同一行
    appendText(kind, delta) {
      if (!delta) return;
      const log = this.state.log;
      const last = log[log.length - 1];
      if (last && last.kind === kind) {
        if (last.text.length < MAX_TEXT) last.text += delta;
      } else {
        this.push({ kind, text: delta.replace(/^\n+/, '') });
      }
      this.scrollTerm();
    },
    findTool(id) {
      for (let i = this.state.log.length - 1; i >= 0; i -= 1) {
        const x = this.state.log[i];
        if (x.kind === 'tool' && x.id === id) return x;
      }
      return null;
    },
    onTermScroll() {
      const el = this.$refs.term;
      if (el) this.stickBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    },
    scrollTerm(force = false) {
      if (!force && !this.stickBottom) return;
      this.$nextTick(() => {
        const el = this.$refs.term;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    onEvent(e) {
      const detail = e && e.detail;
      if (!detail || detail.sessionId !== this.state.sessionId) return;
      // 同一会话的追问会挂到新卡片上;已结束的卡片不再接收
      if (!['starting', 'running'].includes(this.state.status)) return;
      const ev = detail.event || {};
      switch (ev.type) {
        case 'busy':
          this.state.status = 'running';
          break;
        case 'text':
          this.appendText('text', ev.delta || '');
          this.$emit('progress');
          break;
        case 'thinking':
          this.appendText('think', ev.delta || '');
          break;
        case 'tool_start':
          this.push({
            kind: 'tool', id: ev.id, tool: ev.tool, summary: ev.summary || '', detail: ev.detail || '',
            output: '', status: 'running', ms: null, open: false
          });
          this.$emit('progress');
          break;
        case 'tool_output': {
          const item = this.findTool(ev.id);
          if (item) item.output = ev.text || '';
          this.scrollTerm();
          break;
        }
        case 'tool_end': {
          const item = this.findTool(ev.id);
          if (item) {
            item.status = ev.isError ? 'error' : 'ok';
            item.output = ev.output || ev.summary || '';
            item.ms = typeof ev.ms === 'number' ? ev.ms : null;
          }
          this.scrollTerm();
          break;
        }
        case 'retry':
          this.push({ kind: 'retry', attempt: ev.attempt, max: ev.max, text: ev.message || '' });
          break;
        case 'idle':
          if (ev.text) this.msg.content = ev.text;
          this.state.status = ev.aborted ? 'stopped' : ev.error ? 'error' : 'done';
          this.$set(this.state, 'endedAt', Date.now());
          if (ev.error) {
            this.$set(this.state, 'error', ev.error);
            this.$set(this.state, 'errorMessage', ev.errorMessage || '');
            this.push({ kind: 'error', text: ev.errorMessage || ev.error });
          } else {
            this.push({ kind: 'end', text: ev.aborted ? '— 已停止 —' : '— 完成 —' });
          }
          this.state.live = true;
          this.$emit('finished');
          this.loadQuota();
          break;
        case 'error':
          this.$set(this.state, 'error', ev.code || 'model');
          this.$set(this.state, 'errorMessage', ev.message || '');
          this.push({ kind: 'error', text: ev.message || ev.code });
          break;
        case 'exit':
          this.state.live = false;
          if (this.state.status === 'running' || this.state.status === 'starting') {
            this.state.status = 'error';
            this.$set(this.state, 'endedAt', Date.now());
            this.$set(this.state, 'error', this.state.error || 'exit');
            this.$set(this.state, 'errorMessage', this.state.errorMessage || '本地 Agent 意外退出');
            this.push({ kind: 'error', text: this.state.errorMessage });
            this.$emit('finished');
          }
          break;
        default:
      }
    },
    async stop() {
      if (this.controller) return this.controller.stop();
      const b = bridge();
      if (b && this.state.sessionId) await b.abort(this.state.sessionId);
    },
    async openDir() {
      if (this.controller) return this.controller.openDir();
      const b = bridge();
      if (b && this.state.sessionId) await b.openDir(this.state.sessionId);
    },
    answerNormally() {
      this.$emit('answer-normally');
    },
    openLogin() {
      if (window.rainseeBrowser && typeof window.rainseeBrowser.openLogin === 'function') window.rainseeBrowser.openLogin();
    },
    logAsText() {
      return this.log.map((x) => {
        if (x.kind === 'tool') {
          const head = `[${x.status}] ${x.tool} ${x.summary}${x.ms != null ? ` (${this.formatMs(x.ms)})` : ''}`;
          return [head, x.detail && `  > ${x.detail.slice(0, 1500)}`, x.output && `  ${x.output.slice(0, 2000)}`].filter(Boolean).join('\n');
        }
        if (x.kind === 'think') return `(思考) ${x.text}`;
        if (x.kind === 'retry') return `(重试 ${x.attempt}/${x.max}) ${x.text}`;
        if (x.kind === 'error') return `(错误) ${x.text}`;
        return x.text;
      }).join('\n');
    },
    // 执行记录 + 浏览器侧诊断(pi stderr、模型错误原文等,不含密钥)
    async copyLog() {
      const s = this.state;
      const lines = [
        `[本地 Agent ${s.error ? '错误日志' : '执行记录'}] ${new Date().toISOString()}`,
        `状态: ${s.status}  错误: ${s.error || '-'}  ${s.errorMessage || ''}`,
        `任务: ${s.task}`,
        `业务名: ${s.business || '-'}`,
        `工作目录: ${s.workspace || '-'}`,
        `会话: ${s.sessionId || '-'}`,
        `页面: ${location.href.split('?')[0]}  UA: ${navigator.userAgent}`,
        '==== 执行记录 ====',
        this.logAsText()
      ];
      const b = bridge();
      const getDiag = this.controller
        ? () => this.controller.getDiagnostics()
        : b && typeof b.getDiagnostics === 'function' ? () => b.getDiagnostics(s.sessionId) : null;
      if (s.error && s.sessionId && getDiag) {
        try {
          const diag = await getDiag();
          if (diag) lines.push('==== 浏览器诊断 ====', diag);
        } catch (e) {
          lines.push(`(读取浏览器诊断失败: ${e && e.message})`);
        }
      }
      const text = lines.join('\n');
      try {
        await navigator.clipboard.writeText(text);
        this.copyState = 'done';
      } catch (e) {
        // 面板失焦时 clipboard API 会拒绝,退回 execCommand
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        this.copyState = document.execCommand('copy') ? 'done' : 'failed';
        document.body.removeChild(ta);
      }
      setTimeout(() => { this.copyState = ''; }, 2000);
    },
    async loadQuota() {
      const b = bridge();
      // 用户自己的模型不占内置额度,不显示
      if (!b || this.state.modelName || typeof b.getQuota !== 'function') return;
      try {
        const q = await b.getQuota();
        if (q && q.ok) this.quota = q;
      } catch (e) {
        // 额度只是提示,拿不到不影响
      }
    }
  }
};
</script>

<style scoped>
/* 控制台配色固定为深色,不随页面主题变化 */
.local-agent-card {
  --la-bg: #16181d;
  --la-bar: #23262d;
  --la-fg: #d6d9df;
  --la-dim: #8a909c;
  --la-accent: #7aa2f7;
  --la-ok: #6fcf97;
  --la-err: #f47174;
  --la-warn: #e5c07b;
  --la-mono: "Cascadia Mono", "JetBrains Mono", Consolas, "SFMono-Regular", Menlo, "Microsoft YaHei UI", monospace;
  margin: 4px 0 8px;
  font-size: 13px;
}
.la-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 10px 10px 0 0;
  background: var(--la-bar);
  color: var(--la-fg);
  font-size: 12px;
  min-width: 0;
}
.la-dots { display: inline-flex; gap: 5px; flex: none; }
.la-dots i { width: 9px; height: 9px; border-radius: 50%; background: #4a4e57; }
.la-running .la-dots i:nth-child(3), .la-starting .la-dots i:nth-child(3) { background: var(--la-ok); }
.la-error .la-dots i:nth-child(1) { background: var(--la-err); }
.la-done .la-dots i { background: #5b6270; }
.la-bar-title { font-weight: 600; flex: none; }
.la-bar-dir {
  color: var(--la-dim); font-family: var(--la-mono); font-size: 11px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1;
  direction: rtl; text-align: left;
}
.la-bar-time { color: var(--la-dim); font-family: var(--la-mono); font-size: 11px; flex: none; }
.la-bar-model {
  flex: none; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 11px; padding: 0 6px; border-radius: 4px; color: var(--la-accent); border: 1px solid #3a3e47;
}

.la-term {
  background: var(--la-bg);
  color: var(--la-fg);
  font-family: var(--la-mono);
  font-size: 12px;
  line-height: 1.55;
  padding: 10px 12px 12px;
  border-radius: 0 0 10px 10px;
  max-height: 420px;
  min-height: 72px;
  overflow: auto;
  resize: vertical;
}
.la-line { white-space: pre-wrap; word-break: break-word; }
.la-prompt { color: var(--la-dim); margin-bottom: 6px; }
.la-ps { color: var(--la-ok); font-weight: 700; }
.la-arg { color: var(--la-fg); }
.la-text { color: var(--la-fg); margin: 4px 0; }
.la-think { color: var(--la-dim); font-style: italic; margin: 4px 0; }
.la-tag {
  display: inline-block; font-style: normal; font-size: 10px; padding: 0 5px; margin-right: 6px;
  border: 1px solid #3a3e47; border-radius: 4px; color: var(--la-dim);
}
.la-warn { color: var(--la-warn); }
.la-err { color: var(--la-err); margin-top: 4px; }
.la-end { color: var(--la-dim); text-align: center; margin-top: 6px; }
.la-wait { height: 18px; }

.la-tool { margin: 6px 0; }
.la-tool-head { display: flex; align-items: baseline; gap: 8px; cursor: pointer; min-width: 0; }
.la-tool-mark { flex: none; width: 12px; color: var(--la-accent); }
.la-tool-ok .la-tool-mark { color: var(--la-ok); }
.la-tool-error .la-tool-mark { color: var(--la-err); }
.la-tool-name { flex: none; color: var(--la-accent); font-weight: 600; }
.la-tool-sum { color: var(--la-fg); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1; }
.la-tool-ms { flex: none; color: var(--la-dim); font-size: 11px; }
.la-tool-detail, .la-tool-out {
  margin: 4px 0 0 20px;
  padding: 6px 8px;
  border-left: 2px solid #2f333b;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
  overflow: auto;
  font-family: inherit;
  font-size: 11.5px;
}
.la-tool-detail { color: #c0caf5; background: rgba(122, 162, 247, 0.06); }
.la-tool-out { color: var(--la-dim); }
.la-tool-error .la-tool-out { color: var(--la-err); border-left-color: rgba(244, 113, 116, 0.5); }
.la-tool-more { margin: 2px 0 0 20px; color: var(--la-dim); font-size: 11px; cursor: pointer; }
.la-tool-more:hover { color: var(--la-accent); }

.la-cursor {
  display: inline-block; width: 7px; height: 13px; background: var(--la-fg); vertical-align: text-bottom;
  animation: la-blink 1s steps(1) infinite;
}
@keyframes la-blink { 50% { opacity: 0; } }

.la-error { margin-top: 8px; color: #e5484d; }
.la-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 8px; }
.la-btn {
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.15)); background: transparent; color: inherit;
  border-radius: 8px; padding: 4px 10px; cursor: pointer; font-size: 12px;
}
.la-btn:hover { border-color: #6e88f9; color: #6e88f9; }
.la-btn-link { border-color: transparent; opacity: 0.7; }
.la-btn-primary { border-color: #6e88f9; background: #6e88f9; color: #fff; }
.la-btn-primary:hover { color: #fff; opacity: 0.9; }
.la-quota { margin-left: auto; opacity: 0.6; font-size: 12px; }
</style>
